import axios from "axios";
import localStorageService from "./localStorageService";
import { getEnv } from "../envConfig";
import { jwtDecode } from "jwt-decode";

const API_URL = getEnv().ENDPOINT;

/**
 * Login with JWT
 */
export async function jwtLogin({ username, password }) {
  try {
    const response = await axios.post(`${API_URL}/api/users/login`, {
      email: username,
      password,
    });

    const { token, refreshToken } = response.data;

    // Store tokens
    await localStorageService.setItem("access_token", token);
    await localStorageService.setItem("refresh_token", refreshToken);

    // Decode and store user info
    const decoded = jwtDecode(token);
    const roleNames = decoded.roles.map((role) => role.role.name);
    const user = response.data.user;
    user.roles = roleNames || [""];

    await localStorageService.setItem("user", user);

    return { user, accessToken: token, refreshToken };
  } catch (error) {
    throw error.response
      ? error.response.data
      : { errorMessage: "An unexpected error occurred." };
  }
}

/**
 * Logout
 */
export async function jwtLogout() {
  await localStorageService.removeItem("access_token");
  await localStorageService.removeItem("refresh_token");
  await localStorageService.removeItem("user");
}

/**
 * Refresh JWT token
 */
export async function jwtRefreshToken() {
  try {
    const refreshTokenValue = await localStorageService.getItem("refresh_token");
    if (!refreshTokenValue) {
      throw new Error("No refresh token available");
    }

    const response = await axios.post(`${API_URL}/api/users/refresh-token`, {
      refreshToken: refreshTokenValue,
    });

    const { token, refreshToken } = response.data;

    // Store new tokens
    await localStorageService.setItem("access_token", token);
    await localStorageService.setItem("refresh_token", refreshToken);

    return token;
  } catch (error) {
    // If refresh token is expired or invalid
    if (error.response && (error.response.status === 400 || error.response.status === 401)) {
      console.error("Refresh token expired or invalid");
      await localStorageService.removeItem("access_token");
      await localStorageService.removeItem("refresh_token");
      await localStorageService.removeItem("user");
      throw new Error("REFRESH_TOKEN_EXPIRED");
    }
    throw error;
  }
}

/**
 * Get access token from storage
 */
export async function getAccessToken() {
  return await localStorageService.getItem("access_token");
}

/**
 * Get refresh token from storage
 */
export async function getRefreshToken() {
  return await localStorageService.getItem("refresh_token");
}